import actions from 'src/modules/resource/tree/resourceTreeActions';

type TreeNodeData = {
  id: string;
  name?: string;
  shortName?: string;
  type?: string;
  status?: string;
  parentResource?: string | null;
  childrenCount?: number;
  color?: string;
  backgroundColor?: string;
};

type TreeFetchResponse = {
  ancestors: TreeNodeData[];
  siblings: TreeNodeData[];
  focus: TreeNodeData | null;
  children: TreeNodeData[];
};

type ExpansionState = {
  open: boolean;
  loading: boolean;
  children: TreeNodeData[] | null;
};

type State = {
  loading: boolean;
  focusId: string | null;
  data: TreeFetchResponse | null;
  expansions: Record<string, ExpansionState>;
};

const initialData: State = {
  loading: false,
  focusId: null,
  data: null,
  expansions: {},
};

type Action = {
  type: string;
  payload?: {
    focusId?: string | null;
    response?: TreeFetchResponse;
    nodeId?: string;
    children?: TreeNodeData[];
  };
};

export default (
  state: State = initialData,
  { type, payload }: Action,
): State => {
  if (type === actions.RESETED) {
    return { ...initialData };
  }

  if (type === actions.FETCH_STARTED) {
    return {
      ...state,
      loading: true,
      focusId: payload?.focusId ?? null,
    };
  }

  if (type === actions.FETCH_SUCCESS) {
    return {
      ...state,
      loading: false,
      focusId: payload?.focusId ?? null,
      data: payload?.response ?? null,
    };
  }

  if (type === actions.FETCH_ERROR) {
    return {
      ...state,
      loading: false,
      data: null,
    };
  }

  if (type === actions.EXPAND_STARTED) {
    const nodeId = payload?.nodeId;
    if (!nodeId) return state;
    return {
      ...state,
      expansions: {
        ...state.expansions,
        [nodeId]: {
          open: true,
          loading: true,
          children:
            state.expansions[nodeId]?.children ?? null,
        },
      },
    };
  }

  if (type === actions.EXPAND_SUCCESS) {
    const nodeId = payload?.nodeId;
    if (!nodeId) return state;
    return {
      ...state,
      expansions: {
        ...state.expansions,
        [nodeId]: {
          open: true,
          loading: false,
          children: payload?.children ?? [],
        },
      },
    };
  }

  if (type === actions.EXPAND_ERROR) {
    const nodeId = payload?.nodeId;
    if (!nodeId) return state;
    return {
      ...state,
      expansions: {
        ...state.expansions,
        [nodeId]: {
          open: false,
          loading: false,
          children: null,
        },
      },
    };
  }

  if (type === actions.EXPAND_COLLAPSED) {
    const nodeId = payload?.nodeId;
    if (!nodeId) return state;
    const existing = state.expansions[nodeId];
    return {
      ...state,
      expansions: {
        ...state.expansions,
        [nodeId]: {
          open: false,
          loading: false,
          children: existing?.children ?? null,
        },
      },
    };
  }

  return state;
};
